import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Link2, ArrowUpRight } from 'lucide-react';

const Footer = () => {
  const footerLinks = {
    services: [
      { name: 'Fleet & Ground', href: '/ground' },
      { name: 'Equipment', href: '/equipment' },
      { name: 'Cross-Border', href: '/cross-border' },
      { name: 'Global & Tech', href: '/global-tech' },
      { name: 'Get a Quote', href: '/quote' },
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Careers', href: '/careers' },
      { name: 'News', href: '/news' },
      { name: 'Contact', href: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' },
    ],
  };

  return (
    <footer className="bg-forest text-white">
      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <a href="/" className="inline-flex items-center gap-3 mb-6 bg-white rounded-2xl p-4 shadow-lg">
              <div className="h-12 w-auto">
                <img 
                  src="/logo.png" 
                  alt="Quinte Freight" 
                  className="h-full w-auto object-contain"
                />
              </div>
            </a>
            <div className="mb-4">
              <span className="inline-block font-display font-bold text-xl text-amber bg-forest/20 px-4 py-2 rounded-lg border border-amber/30">
                Global Solutions Local Excellence
              </span>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 max-w-sm">
              Engineering unrelenting capacity across North America. Your trusted partner for end-to-end freight brokerage and customs solutions. Global Solutions Local Excellence.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-forest-2 flex items-center justify-center text-white/60 hover:text-amber hover:bg-forest-3 transition-all"
              >
                <Link2 className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-sm uppercase tracking-wider text-amber mb-4">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-amber transition-colors text-sm font-medium"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-sm uppercase tracking-wider text-amber mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-amber transition-colors text-sm font-medium"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-sm uppercase tracking-wider text-amber mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-amber transition-colors text-sm font-medium"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-sm uppercase tracking-wider text-amber mb-4">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm">
                  Belleville, Ontario<br />Canada
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-amber flex-shrink-0" />
                <a href="tel:+1-800-555-0123" className="text-white/60 hover:text-amber transition-colors text-sm">
                  1-800-555-0123
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber flex-shrink-0" />
                <a href="mailto:info@quintefreight.com" className="text-white/60 hover:text-amber transition-colors text-sm">
                  info@quintefreight.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-forest-2">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © 2026 Quinte Freight Logistics. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-white/40 text-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-amber rounded-full animate-pulse"></span>
                Fully Licensed • DOT • CBSA
              </span>
              <span className="text-white/40 text-sm">
                Designed & Developed by 
                <a 
                  href="https://saibhang.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber hover:text-white transition-colors ml-1 font-medium"
                >
                  Saibhang.com
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
